import {
    IconAnimals, IconBriefcase, IconCell, IconChart, IconCheckCircle, IconClose,
    IconDroplet, IconHospital, IconLineage, IconSperm,
} from '../icons';
import { FUNNEL_COLORS, FUNNEL_LABELS, LOW_COHORT_N } from '../../core/constants/kpis';
import { fmtNum, fmtPct } from '../../core/utils/format';

import '../styles/stats-section.css';

// TSS vite/01 — bloque de indicadores reproductivos compartido, PORT del
// StatsView del dashboard (cowre.ultimate.dashboard/src/features/stats/
// layouts/entity-stats.jsx + kpis/layouts/{production,rates}.jsx): mismas
// clases (`report-*` / `estats-*`), mismos estilos y mismos SVGs — directiva
// del arquitecto. Promovido al 3er consumidor (parcerias/detail + vacas/detail
// + informes; deuda D-002).
//
// Presentacional puro: el caller decide qué production/funnel/rates pintar y
// con qué config (PRODUCTION_CARDS / RECIPIENT_PRODUCTION_CARDS / RATE_ROWS /
// RECIPIENT_RATE_ROWS). Reglas de oro:
//  · el front NUNCA recalcula: mapea key→label/icono y pinta el `qualifier`
//    del backend (semáforo por [data-qualifier] en CSS);
//  · contadores y tasas se FILTRAN a las keys presentes (donors 4 tasas,
//    recipients 2 — nunca asumir el set del admin);
//  · embudo de barras horizontales, width = total / max(total) — el canal
//    público no envía `funnel.display`;
//  · width y color por etapa (FUNNEL_COLORS) son estilo POR DATO (inline ok).

const ICONS = {
    briefcase: IconBriefcase,
    droplet:   IconDroplet,
    sperm:     IconSperm,
    cell:      IconCell,
    lineage:   IconLineage,
    animal:    IconAnimals,
    hospital:  IconHospital,
    close:     IconClose,
    check:     IconCheckCircle,
};

export default function StatsSection({
    title,
    production,
    productionConfig = [],
    funnel,
    funnelLabels = FUNNEL_LABELS,
    rates,
    rateRows = [],
    lowCohortN = LOW_COHORT_N,
    emptyText = 'Sin datos suficientes para calcular indicadores.',
}) {
    const counts = production || {};
    const stages = funnel?.stages || [];
    const rateMap = rates || {};

    const prodCards = productionConfig.filter((card) => counts[card.key] != null);
    const rows = rateRows.filter((row) => row.key in rateMap && rateMap[row.key]);

    if (!prodCards.length && !stages.length && !rows.length) {
        return <p className="estats__muted">{emptyText}</p>;
    }

    const maxTotal = stages.reduce((m, s) => Math.max(m, Number(s.total) || 0), 0);
    const isLowCohort = rows.some((row) => {
        const cohort = rateMap[row.key]?.cohort_n;
        return cohort != null && cohort > 0 && cohort < lowCohortN;
    });

    return (
        <div className="stats-section">
            {title && <h3 className="stats-section__title">{title}</h3>}

            {prodCards.length > 0 && (
                <div className="report-totals">
                    {prodCards.map((card) => {
                        const Icon = ICONS[card.icon] || IconChart;
                        return (
                            <article key={card.key} className="report-total-card" data-accent={card.accent}>
                                <span className="report-total-card__icon"><Icon /></span>
                                <span className="report-total-card__body">
                                    <span className="report-total-card__value">{fmtNum(counts[card.key])}</span>
                                    <span className="report-total-card__label">{card.label}</span>
                                </span>
                                <span className="report-total-card__bar" />
                            </article>
                        );
                    })}
                </div>
            )}

            <div className="estats__grid">
                <article className="report-card estats-funnel">
                    <h2 className="report-section__title">Embudo</h2>
                    <ul className="estats-funnel__list">
                        {stages.map((s) => {
                            const w = maxTotal ? Math.round(((Number(s.total) || 0) / maxTotal) * 100) : 0;
                            return (
                                <li key={s.key} className="estats-funnel__row">
                                    <span className="estats-funnel__label">{funnelLabels[s.key] || s.key}</span>
                                    <span className="estats-funnel__bar">
                                        <span
                                            className="estats-funnel__fill"
                                            style={{ width: `${w}%`, background: FUNNEL_COLORS[s.key] || '#94a3b8' }}
                                        />
                                    </span>
                                    <span className="estats-funnel__total">{fmtNum(s.total)}</span>
                                    <span className="estats-funnel__rate">
                                        {s.rate_vs_previous == null ? '—' : fmtPct(s.rate_vs_previous)}
                                    </span>
                                </li>
                            );
                        })}
                        {!stages.length && <li className="estats-funnel__empty">Sin datos.</li>}
                    </ul>
                </article>

                <article className="report-card report-rates">
                    <h2 className="report-section__title">Tasas del proceso</h2>
                    <div className="report-rates__grid">
                        {rows.map((row) => {
                            const r = rateMap[row.key] || {};
                            const width = Math.max(0, Math.min(100, Number(r.value) || 0));
                            return (
                                <div key={row.key} className="report-rate" data-qualifier={r.qualifier || 'none'}>
                                    <div className="report-rate__head">
                                        <span className="report-rate__label">{row.label}</span>
                                        <span className="report-rate__value">{fmtPct(r.value)}</span>
                                    </div>
                                    <div className="report-rate__track">
                                        <span className="report-rate__fill" style={{ width: `${width}%` }} />
                                    </div>
                                    <div className="report-rate__sub">
                                        <span>{fmtNum(r.numerator)} / {fmtNum(r.denominator)}</span>
                                        {row.late && r.cohort_n != null && <span>cohorte {fmtNum(r.cohort_n)}</span>}
                                    </div>
                                </div>
                            );
                        })}
                        {!rows.length && <p className="estats__muted">Sin tasas.</p>}
                    </div>
                </article>
            </div>

            {isLowCohort && (
                <p className="stats-section__hint">
                    Con pocos datos la tasa es poco representativa — los conteos son la señal confiable.
                </p>
            )}
        </div>
    );
}
